import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Shield, LogIn, AlertCircle } from "lucide-react";
import { LangToggle } from "@/components/LangToggle";
import { toast } from "sonner";
import { api } from "@/lib/api";

const DEMO = [
  { role: "State", email: "state@hsps.gov.in", password: "Demo@1234" },
  { role: "District", email: "district@hsps.gov.in", password: "Demo@1234" },
  { role: "Secretary", email: "secretary@hsps.gov.in", password: "Demo@1234" },
  { role: "Sarpanch", email: "sarpanch@hsps.gov.in", password: "Demo@1234" },
  { role: "Citizen", email: "citizen@hsps.gov.in", password: "Demo@1234" },
];

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [panchayats, setPanchayats] = useState([]);
  const [pid, setPid] = useState(localStorage.getItem("hsps_panchayat") || "");
  const [fy, setFy] = useState(localStorage.getItem("hsps_fy") || "2025-26");
  const [mode, setMode] = useState(localStorage.getItem("hsps_mode") || "gp");

  useEffect(() => {
    api.get("/panchayats").then((r) => setPanchayats(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    setErr("");
    try {
      localStorage.setItem("hsps_panchayat", pid);
      localStorage.setItem("hsps_fy", fy);
      localStorage.setItem("hsps_mode", mode);
      await login(email, password);
      toast.success("Signed in successfully");
      nav("/dashboard");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FDFBF7]">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative flex-col p-12 text-white" style={{ background: "#0A3161" }}>
        <div className="ashoka-stripe absolute top-0 left-0" />
        <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
          <div className="w-10 h-10 rounded-md bg-[#E87722] flex items-center justify-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{t("govOfHaryana")}</div>
            <div className="font-heading font-bold text-base">{t("appName")}</div>
          </div>
        </Link>
        <div className="flex-1 flex items-center">
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">{t("tagline")}</div>
            <h1 className="mt-3 font-heading text-4xl font-bold leading-tight">{t("heroTitle")}</h1>
            <p className="mt-6 text-white/80 leading-relaxed">{t("heroSub")}</p>
          </div>
        </div>
        <div className="text-xs text-white/50">{t("digitalSignature")}</div>
      </div>

      {/* Right login panel */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex justify-between items-center mb-8">
            <Link to="/" className="text-sm text-slate-600 hover:text-[#0A3161]">← {t("home")}</Link>
            <LangToggle />
          </div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{t("citizenSignIn")}</div>
          <h2 className="mt-3 font-heading text-3xl font-bold text-slate-900">{t("enterCredentials")}</h2>

          {err && (
            <div className="mt-6 flex items-start gap-2 p-3 rounded-md border border-red-200 bg-red-50 text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
            </div>
          )}

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t("email")}</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0A3161] focus:ring-2 focus:ring-[#0A3161]/10 outline-none text-sm"
                placeholder="you@hsps.gov.in"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t("password")}</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0A3161] focus:ring-2 focus:ring-[#0A3161]/10 outline-none text-sm"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md font-semibold text-sm text-white transition-colors hover:-translate-y-[1px] disabled:opacity-50"
              style={{ background: "#0A3161" }}
            >
              <LogIn className="w-4 h-4" /> {busy ? "…" : t("login")}
            </button>

            {/* Digital G-Book: Village + FY + Mode */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-dashed border-slate-200">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("village")}</label>
                <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="login-village"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
                  <option value="">— Select —</option>
                  {panchayats.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.block}, {p.district}</option>)}
                </select>
                {pid && (() => {
                  const p = panchayats.find((x) => x.id === pid);
                  return p ? <div className="mt-1 text-[10px] text-slate-500">Taluka: <span className="font-semibold">{p.block}</span> · District: <span className="font-semibold">{p.district}</span></div> : null;
                })()}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("fy")}</label>
                <select value={fy} onChange={(e) => setFy(e.target.value)} data-testid="login-fy"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm font-mono">
                  {["2025-26", "2024-25", "2023-24", "2026-27"].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{t("mode")}</label>
                <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="login-mode"
                  className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
                  <option value="gp">{t("gpMode")}</option>
                  <option value="jsk">{t("jskMode")}</option>
                </select>
              </div>
            </div>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            {t("noAccount")} <Link to="/register" className="text-[#0A3161] font-semibold hover:underline" data-testid="link-register">{t("register")}</Link>
          </div>

          <div className="mt-10 border-t border-slate-200 pt-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">{t("demoAccounts")}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.email}
                  type="button"
                  data-testid={`demo-${d.role.toLowerCase()}`}
                  onClick={() => fillDemo(d)}
                  className="text-left p-2.5 rounded-md border border-slate-200 hover:border-[#0A3161] hover:bg-[#0A3161]/5 transition-colors text-xs"
                >
                  <div className="font-semibold text-slate-900">{d.role}</div>
                  <div className="text-slate-500 font-mono text-[11px] truncate">{d.email}</div>
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-3">Password: <span className="font-mono">Demo@1234</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
